import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { client } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { HeartPulse, Check, Sparkles } from "lucide-react";

const MOOD = ["Very low", "Low", "Okay", "Good", "Peaceful"];
const CRAVE = ["None", "Mild", "Moderate", "Strong", "Intense"];
const ENERGY = ["Drained", "Tired", "Steady", "Energized", "Vibrant"];

const ScaleRow = ({ label, labels, value, onChange, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <Label className="text-olive">{label}</Label>
      <span className="text-sm font-medium text-sky">{labels[value - 1]}</span>
    </div>
    <Slider min={1} max={5} step={1} value={[value]} onValueChange={(v) => onChange(v[0])} className="py-2" />
  </div>
);

export default function CheckInPage() {
  const [form, setForm] = useState({ mood: 3, craving_level: 3, energy: 3, stayed_clean: true, notes: "", gratitude: "" });
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedToday, setSavedToday] = useState(false);

  const load = () => {
    client.get("/checkins/today").then((r) => {
      if (r.data && r.data.date) {
        setForm({
          mood: r.data.mood, craving_level: r.data.craving_level, energy: r.data.energy,
          stayed_clean: r.data.stayed_clean, notes: r.data.notes || "", gratitude: r.data.gratitude || "",
        });
        setSavedToday(true);
      }
    });
    client.get("/checkins").then((r) => setHistory(r.data));
  };
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      await client.post("/checkins", form);
      toast.success(form.stayed_clean ? "Well done. Every faithful day matters." : "Thank you for your honesty. Grace covers today too.");
      setSavedToday(true);
      client.get("/checkins").then((r) => setHistory(r.data));
    } catch (e) {
      toast.error("Could not save your check-in.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="checkin-page">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs text-terracotta mb-2">One honest moment</p>
        <h1 className="font-serif text-display text-olive">Daily check-in</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2 rounded-3xl bg-card border border-border p-7 shadow-soft space-y-7">
          <div className="flex items-center justify-between rounded-2xl bg-sage p-5" data-testid="stayed-clean-row">
            <div>
              <p className="font-medium text-olive">Did you stay true to your plan today?</p>
              <p className="text-sm text-stone">There is no shame here — only honesty and grace.</p>
            </div>
            <Switch data-testid="stayed-clean-switch" checked={form.stayed_clean} onCheckedChange={(v) => setForm((f) => ({ ...f, stayed_clean: v }))} />
          </div>

          <ScaleRow testid="scale-mood" label="Mood" labels={MOOD} value={form.mood} onChange={(v) => setForm((f) => ({ ...f, mood: v }))} />
          <ScaleRow testid="scale-craving" label="Craving intensity" labels={CRAVE} value={form.craving_level} onChange={(v) => setForm((f) => ({ ...f, craving_level: v }))} />
          <ScaleRow testid="scale-energy" label="Energy" labels={ENERGY} value={form.energy} onChange={(v) => setForm((f) => ({ ...f, energy: v }))} />

          <div>
            <Label htmlFor="gratitude" className="text-olive mb-2 block">One thing I'm grateful to God for</Label>
            <Textarea id="gratitude" data-testid="input-gratitude" rows={2} placeholder="A small mercy from today…" value={form.gratitude}
              onChange={(e) => setForm((f) => ({ ...f, gratitude: e.target.value }))} />
          </div>
          <div>
            <Label htmlFor="notes" className="text-olive mb-2 block">Notes (triggers, wins, anything)</Label>
            <Textarea id="notes" data-testid="input-notes" rows={3} placeholder="What today felt like…" value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>

          <Button data-testid="save-checkin-btn" disabled={saving} onClick={save} className="w-full rounded-full bg-terracotta hover:bg-terracotta-soft text-sand gap-2 py-6">
            {saving ? "Saving…" : savedToday ? <><Check className="w-4 h-4" /> Update today's check-in</> : <>Save today's check-in</>}
          </Button>
        </motion.div>

        <div className="rounded-3xl bg-card border border-border p-7 shadow-soft" data-testid="checkin-history">
          <h3 className="font-serif text-2xl text-olive mb-4">Recent days</h3>
          {history.length === 0 ? (
            <p className="text-sm text-stone">Your reflections will appear here.</p>
          ) : (
            <div className="space-y-3">
              {history.slice(0, 10).map((h) => (
                <div key={h.date} className="flex items-center gap-3 rounded-2xl bg-sand p-3.5" data-testid={`history-${h.date}`}>
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${h.stayed_clean ? "bg-gold" : "bg-stone/40"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-olive">{new Date(h.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</p>
                    {h.gratitude && <p className="text-xs text-stone truncate italic">“{h.gratitude}”</p>}
                  </div>
                  <span className="text-xs text-sky">{MOOD[h.mood - 1]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
